import { CalendarDays } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  CALENDAR_SOURCE_LABELS,
  SUPPORTED_TIMEZONES,
  describeWorkingDays,
  formatClockRange,
  type WorkCalendarSummary,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { TimePicker } from '../../../components/TimePicker';
import { WorkingDayPicker } from '../../../components/WorkingDayPicker';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { isSameDraft, rebasedDraft } from '../../../lib/draft-rebase';
import { formatNumber } from '../../../lib/format';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useDepartments } from '../../organisation/api/organisation-api';
import { useCalendars, useHolidays, useResolvedCalendar, useUpdateCalendar } from '../api/calendars-api';
import { HolidayPanel } from '../components/HolidayPanel';
import { AddCalendarButton } from '../components/calendars/AddCalendarButton';
import { RemoveCalendarButton } from '../components/calendars/RemoveCalendarButton';
import { YearCard } from '../components/calendars/YearCard';
import { todayIn } from '../components/calendars/calendar-year';
import { useHolidayActions } from '../components/calendars/use-holiday-actions';
import styles from './CalendarsPage.module.css';

/**
 * ADMIN-10 — working hours and the holiday calendar. F16.09. Option A of the admin redesign
 * (`5-settings/calendars.html`, "Year at a glance"): the calendars on the left, the whole year in the
 * middle, the hours and the holiday list on the right. Holidays are a once-a-year job, and the year
 * shows a missing or wrong date at a glance. Admin and HR (`hiring_setup`, ADR 0073).
 *
 * Why this screen matters more than it looks: every SLA clock in the system excludes non-working
 * time, so what is configured here decides whether a number the product reports is defensible.
 */
export function CalendarsPage(): ReactNode {
  const canView = useCanView('hiring_setup');
  const canEdit = useCanEdit('hiring_setup');
  const calendars = useCalendars(canView);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (!canView) return <ForbiddenState resource="calendars" contact="Your admin" />;

  const rows = calendars.data ?? [];
  // Derived during render rather than corrected by an effect: the first calendar selects itself, and a
  // calendar just removed falls back to the first without a second render. `selectedId` records only a click.
  const selected = rows.find((row) => row.id === selectedId) ?? rows[0] ?? null;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h1 className={styles.title}>Calendars</h1>
          <p className={styles.lede}>Which days and hours each part of the company works. Every time limit skips the days and hours that are off.</p>
        </div>
        {canEdit && <AddCalendarButton onCreated={setSelectedId} />}
      </header>

      {calendars.error && !calendars.data ? (
        // A failed load is a failed load, never "No calendars yet".
        <ServerErrorState onRetry={() => void calendars.refetch()} />
      ) : (
        <div className={styles.layout}>
          <div className={styles.listColumn}>
            <Card title="Calendars" meta={calendars.data ? `${formatNumber(rows.length)} in all` : undefined} isFlush>
              {calendars.isPending ? (
                <div className={styles.listPad}>
                  <SkeletonLines count={4} />
                </div>
              ) : rows.length === 0 ? (
                <div className={styles.listPad}>
                  <EmptyState title="No calendars yet" body="Add one to say when a department works. Until then every clock uses the standard week." action={null} />
                </div>
              ) : (
                <ul className={styles.list}>
                  {rows.map((row) => (
                    <li key={row.id}>
                      <button type="button" className={styles.row} aria-current={row.id === selected?.id ? 'true' : undefined} onClick={() => setSelectedId(row.id)}>
                        <span className={styles.rowTop}>
                          <span className={styles.rowName}>{row.name}</span>
                          {row.isDefault ? (
                            <Chip tone="progress" emphasis="solid">
                              Default
                            </Chip>
                          ) : (
                            <span className={styles.count} aria-label={`${row.holidayCount} ${row.holidayCount === 1 ? 'holiday' : 'holidays'}`}>
                              {formatNumber(row.holidayCount)}
                            </span>
                          )}
                        </span>
                        <span className={styles.rowScope}>{scopeOf(row)}</span>
                        <span className={styles.rowHours}>
                          {describeWorkingDays(row.workingDays)} · {formatClockRange(row.startTime, row.endTime)} · {row.timezone}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <ResolutionCheck />
          </div>

          {selected ? (
            <CalendarWorkspace key={selected.id} calendar={selected} canEdit={canEdit} />
          ) : calendars.isPending ? (
            <>
              <div className={styles.year}>
                <Card>
                  <SkeletonLines count={8} />
                </Card>
              </div>
              <div className={styles.side}>
                <Card title="Working hours">
                  <SkeletonLines count={5} />
                </Card>
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** "Everywhere", "Nest Digital", "Nest Digital · Commercial". */
function scopeOf(calendar: WorkCalendarSummary): string {
  if (calendar.isDefault) return 'Everywhere, unless something more specific applies';
  if (calendar.departmentName) return `${calendar.ventureName ?? 'Venture'} · ${calendar.departmentName}`;
  if (calendar.ventureName) return calendar.ventureName;
  return 'Not attached to anything yet';
}

// --- One calendar: its year, its hours and its holidays ----------------------------------------

/**
 * The year is shared by the year view and the holiday list. Remounted per calendar (`key`), so each
 * calendar opens on its own "today" year. "Today" is read once, where the calendar is.
 */
function CalendarWorkspace({ calendar, canEdit }: { readonly calendar: WorkCalendarSummary; readonly canEdit: boolean }): ReactNode {
  const [now] = useState(() => new Date());
  const today = todayIn(calendar.timezone, now);
  const thisYear = Number(today.slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const holidays = useHolidays(calendar.id, year);
  // Warms the other year on the switch, so flipping between this year and next is instant.
  useHolidays(calendar.id, year === thisYear ? thisYear + 1 : thisYear);
  const actions = useHolidayActions(calendar);

  const isCurrent = holidays.data !== undefined && !holidays.isPlaceholderData;
  const yearHolidays = (holidays.data?.holidays ?? []).filter((holiday) => holiday.holidayDate.startsWith(`${year}-`));

  return (
    <>
      <div className={styles.year}>
        {holidays.error && !holidays.data ? (
          <Card title={`${calendar.name} · ${year}`}>
            <ServerErrorState onRetry={() => void holidays.refetch()} />
          </Card>
        ) : (
          <YearCard
            calendar={calendar}
            year={year}
            onYearChange={setYear}
            holidayYears={holidays.data?.years ?? []}
            holidays={yearHolidays}
            isCurrent={isCurrent}
            today={today}
            canEdit={canEdit}
            actions={actions}
          />
        )}
      </div>
      <div className={styles.side}>
        <CalendarEditor calendar={calendar} canEdit={canEdit} />
        <HolidayPanel calendar={calendar} year={year} onYearChange={setYear} holidays={yearHolidays} isLoading={!isCurrent} today={today} canEdit={canEdit} actions={actions} />
      </div>
    </>
  );
}

// --- The hours ----------------------------------------------------------------------------------

/**
 * One calendar's settings. Deliberately **not** autosaving: five fields, each changing how time is
 * measured for a whole department the moment it lands. A half-typed closing time saved between
 * keystrokes is worse than a save button.
 *
 * The form starts from the record and is remounted per calendar. A refetch landing while it is open
 * does not overwrite what is on screen (§5a rule 2), and **a failed save keeps what was typed**
 * (06-qol §4) — the toast says why, and the record underneath refreshes so Discard shows the latest.
 */
function CalendarEditor({ calendar, canEdit }: { readonly calendar: WorkCalendarSummary; readonly canEdit: boolean }): ReactNode {
  const update = useUpdateCalendar();
  const showToast = useToasts((state) => state.show);
  // Review W7: the record the draft started from, and its version. The save is checked against that version, so another
  // Admin's save in between is refused, not overwritten; "changed" is measured from it, so their save alone never makes
  // this form look edited.
  const [base, setBase] = useState(() => ({ version: calendar.version, draft: toDraft(calendar) }));
  const [draft, setDraft] = useState(base.draft);
  const isDirty = !isSameDraft(draft, base.draft);
  // A newer record with nothing typed here, or typed to read just like it: follow it (derived during render).
  if (calendar.version !== base.version && !update.isPending && (!isDirty || isSameDraft(draft, toDraft(calendar)))) {
    const latest = { version: calendar.version, draft: toDraft(calendar) };
    setBase(latest);
    setDraft(latest.draft);
  }
  // Our own save's refetch lands before its answer is handled; it is not "someone else".
  const isOutdated = isDirty && !update.isPending && calendar.version !== base.version;
  const closesTooEarly = draft.endTime <= draft.startTime;
  const isReady = isDirty && draft.name.trim() !== '' && draft.workingDays !== 0 && !closesTooEarly;

  const send = (values: CalendarDraft, version: number): void => {
    update.mutate(
      { id: calendar.id, ...values, name: values.name.trim(), version },
      {
        onSuccess: (saved) => {
          const latest = { version: saved.version, draft: toDraft(saved) };
          setBase(latest);
          setDraft(latest.draft);
          showToast({ tone: 'success', message: `${saved.name} saved` });
        },
        onError: (error) => showToast({ tone: 'danger', message: "The changes weren't saved", detail: `${error.message} What you typed is still here.` }),
      },
    );
  };

  const save = (): void => {
    if (!isReady || update.isPending) return;
    if (!isOutdated) {
      send(draft, base.version);
      return;
    }
    // "Save mine over theirs": their record is the new base, only what was changed here goes on top of it.
    const theirs = { version: calendar.version, draft: toDraft(calendar) };
    const mine = rebasedDraft(draft, base.draft, theirs.draft);
    setBase(theirs);
    setDraft(mine);
    send(mine, theirs.version);
  };

  const showLatest = (): void => {
    const latest = { version: calendar.version, draft: toDraft(calendar) };
    setBase(latest);
    setDraft(latest.draft);
  };

  return (
    <Card title="Working hours" meta={calendar.isDefault ? 'The fallback for everything' : scopeOf(calendar)}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            save();
          }
        }}
      >
        <Field label="Name" isRequired>
          {({ inputId }) => <Input id={inputId} maxLength={120} disabled={!canEdit} value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />}
        </Field>

        <Field label="Working days" hint="No clock runs on the other days.">
          {({ inputId, describedBy }) => <WorkingDayPicker id={inputId} describedBy={describedBy} value={draft.workingDays} disabled={!canEdit} onChange={(workingDays) => setDraft({ ...draft, workingDays })} />}
        </Field>

        <div className={styles.hours}>
          <Field label="Opens" isRequired>
            {({ inputId }) => <TimePicker id={inputId} label="Opens" disabled={!canEdit} value={draft.startTime} onChange={(startTime) => setDraft({ ...draft, startTime })} />}
          </Field>
          <Field label="Closes" isRequired error={closesTooEarly ? 'Later than the opening time.' : undefined}>
            {({ inputId }) => <TimePicker id={inputId} label="Closes" disabled={!canEdit} value={draft.endTime} onChange={(endTime) => setDraft({ ...draft, endTime })} />}
          </Field>
        </div>

        <Field label="Timezone" hint="The hours are read in this timezone; daylight saving is applied by itself.">
          {({ inputId, describedBy }) => (
            <Select
              id={inputId}
              describedBy={describedBy}
              label="Timezone"
              disabled={!canEdit}
              value={draft.timezone}
              options={SUPPORTED_TIMEZONES.map((zone) => ({ value: zone.value, label: zone.label }))}
              onChange={(timezone) => setDraft({ ...draft, timezone })}
            />
          )}
        </Field>

        {canEdit && isOutdated && (
          <p className={styles.outdated} role="status">
            Someone else saved this calendar while you were editing. Save puts only your changes on top of theirs; Discard shows theirs.
          </p>
        )}
        {canEdit && (
          <div className={styles.actions}>
            <Button type="submit" variant="solid" isPending={update.isPending} disabled={!isReady}>
              {isOutdated ? 'Save mine over theirs' : 'Save changes'}
            </Button>
            {isDirty && (
              <Button type="button" variant="ghost" onClick={showLatest}>
                Discard
              </Button>
            )}
            <span className={styles.spacer} />
            {calendar.isDefault ? <p className={styles.note}>The standard week can’t be removed: every clock falls back to it.</p> : <RemoveCalendarButton calendar={calendar} />}
          </div>
        )}
      </form>
    </Card>
  );
}

interface CalendarDraft {
  readonly name: string;
  readonly timezone: string;
  readonly workingDays: number;
  readonly startTime: string;
  readonly endTime: string;
  readonly ventureId: string | null;
  readonly departmentId: string | null;
}

function toDraft(calendar: WorkCalendarSummary): CalendarDraft {
  return {
    name: calendar.name,
    timezone: calendar.timezone,
    workingDays: calendar.workingDays,
    startTime: calendar.startTime,
    endTime: calendar.endTime,
    ventureId: calendar.ventureId,
    departmentId: calendar.departmentId,
  };
}

// --- "Which calendar applies here?" -------------------------------------------------------------

/**
 * Under the list, where the inheritance question is asked. Inheritance that cannot be seen is
 * inheritance nobody can check: this answers which calendar a department's clocks run on, and why.
 */
function ResolutionCheck(): ReactNode {
  const departments = useDepartments();
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const resolved = useResolvedCalendar({ departmentId });

  return (
    <Card title="Which calendar applies">
      <div className={styles.resolve}>
        <Select
          label="Department"
          placeholder="Choose a department"
          value={departmentId}
          options={(departments.data ?? []).map((department) => ({ value: department.id, label: department.name }))}
          onChange={setDepartmentId}
        />
        {resolved.data ? (
          <div className={styles.resolved}>
            <span className={styles.resolvedName}>
              <CalendarDays size={14} aria-hidden /> {resolved.data.calendar.name}
            </span>
            <span className={styles.resolvedMeta}>
              {describeWorkingDays(resolved.data.calendar.workingDays)} · {formatClockRange(resolved.data.calendar.startTime, resolved.data.calendar.endTime)}
            </span>
            <Chip tone="neutral">{CALENDAR_SOURCE_LABELS[resolved.data.source]}</Chip>
          </div>
        ) : (
          <p className={styles.note}>Pick a department to see which calendar its clocks run on, and where it comes from.</p>
        )}
      </div>
    </Card>
  );
}
